import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Layout from './components/Layout';
import LoginScreen from './screens/LoginScreen';
import FirstAdminSetupScreen from './screens/FirstAdminSetupScreen';
import DashboardScreen from './screens/DashboardScreen';
import UserManagementScreen from './screens/UserManagementScreen';
import LeadManagementScreen from './screens/LeadManagementScreen';
import CommissionsScreen from './screens/CommissionsScreen';
import SettingsScreen from './screens/SettingsScreen';
import AnalyticsScreen from './screens/AnalyticsScreen';
import SupportScreen from './screens/SupportScreen';
import { Box, Typography, Button } from '@mui/material';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { adminUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return adminUser ? <>{children}</> : <Navigate to="/login" />;
};

const FirstAdminSetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isFirstAdmin, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isFirstAdmin ? <>{children}</> : <Navigate to="/dashboard" />;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <NotificationProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<LoginScreen />} />
                <Route
                  path="/setup"
                  element={
                    <FirstAdminSetupRoute>
                      <FirstAdminSetupScreen />
                    </FirstAdminSetupRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DashboardScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserManagementScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <LeadManagementScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/commissions"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CommissionsScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SettingsScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <AnalyticsScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/support"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SupportScreen />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="*" element={
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h4">Page non trouvée</Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      L'URL demandée n'existe pas.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => window.location.href = '/dashboard'}
                    >
                      Aller au tableau de bord
                    </Button>
                  </Box>
                } />
              </Routes>
            </Router>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
